import * as React from 'react';
import {HStack, Text, VStack} from '@packages/systems/spring';
import {
  CODE_COMPONENT_TEMPLATES,
  type CodeComponentTemplate,
  type CodeComponentTemplateId,
} from '@packages/systems/code-libraries/core';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';
import {emotionStyled} from '@packages/utilities/styled';
import {CODE_COMPONENT_TEMPLATE_VIDEOS} from './codeComponentTemplateVideos';

const CREATOR_TEMPLATE_IDS: readonly CodeComponentTemplateId[] = [
  'pricing-calculator',
  'quotes-carousel',
  'multistep-form',
];

const codeComponentCreatorTemplates = CODE_COMPONENT_TEMPLATES.filter(
  (template) => CREATOR_TEMPLATE_IDS.includes(template.id)
);

// Seconds just before each clip starts moving. The frames before match the
// thumbnail, so hover skips the still intro instead of seeming to lag.
export const CREATOR_TEMPLATE_PREVIEW_START: Partial<
  Record<CodeComponentTemplateId, number>
> = {
  'pricing-calculator': 2.7,
  'quotes-carousel': 0.85,
};

const TemplateButton = emotionStyled('button')({
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
  flex: 1,
  minWidth: 0,
  padding: 0,
  border: 'none',
  background: 'none',
  textAlign: 'start',
  cursor: 'pointer',
  ':focus-visible': {
    outline: '1px solid $blueBorder',
    outlineOffset: '1px',
  },
  '&:hover video, &:focus-visible video': {
    transform: 'scale(1.1)',
  },
});

// Clips the zoomed video so it grows inside the card's frame.
const PreviewFrame = emotionStyled('div')({
  overflow: 'hidden',
  borderRadius: '$2',
});

const PreviewVideo = emotionStyled('video')({
  display: 'block',
  width: '100%',
  aspectRatio: '200 / 96',
  objectFit: 'cover',
  transition: 'transform 250ms ease-out',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
});

function TemplateCard({
  template,
  onSelect,
}: {
  template: CodeComponentTemplate;
  onSelect: (template: CodeComponentTemplate) => void;
}) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const play = () => {
    const video = videoRef.current;
    if (!video || prefersReducedMotion) return;
    if (video.paused) {
      video.currentTime = CREATOR_TEMPLATE_PREVIEW_START[template.id] ?? 0;
    }
    // Leaving before playback starts rejects play(); the rewind covers it.
    video.play().catch(() => {});
  };
  const rewind = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  };

  return (
    <TemplateButton
      type="button"
      onClick={() => onSelect(template)}
      onMouseEnter={play}
      onMouseLeave={rewind}
      onFocus={play}
      onBlur={rewind}
      data-automation-id={`code-component-creator-template-${template.id}`}
    >
      <PreviewFrame>
        <PreviewVideo
          ref={videoRef}
          aria-hidden
          loop
          muted
          playsInline
          // The first frame is the card's thumbnail, so load it up front.
          preload="auto"
          src={CODE_COMPONENT_TEMPLATE_VIDEOS[template.id]}
        />
      </PreviewFrame>
      <Text colorVariant="primary">{template.name}</Text>
    </TemplateButton>
  );
}

export function CodeComponentCreatorTemplates({
  onSelect,
}: {
  onSelect: (template: CodeComponentTemplate) => void;
}) {
  return (
    <VStack spacing={3} align="stretch" paddingTop="$2">
      <Text colorVariant="secondary">Get started with a template →</Text>
      <HStack spacing={5} align="start">
        {codeComponentCreatorTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onSelect={onSelect}
          />
        ))}
      </HStack>
    </VStack>
  );
}
